import { addMonths } from 'date-fns/addMonths'
import { isBefore } from 'date-fns/isBefore'
import { startOfMonth } from 'date-fns/startOfMonth'
import { subMonths } from 'date-fns/subMonths'
import { endOfMonth } from 'date-fns/endOfMonth'

import { CategoryGuids, DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { MonthlyCategoryTotals } from '../../core'
import { isIncome, isPayment, isSpend } from './shared'

type DateRange = {
  start: Date
  end: Date
}

export const getMonthlyTotals = (totals: MonthlyCategoryTotals[], range?: DateRange) => {
  const data: { x: string; y: number }[] = []
  let startDate = startOfMonth(range?.start ?? subMonths(new Date(), 5))
  const endDate = range?.end ?? new Date()

  while (isBefore(startDate, endDate)) {
    // Add total amount for each month
    data.push({
      x: formatDate(startDate, DATE_FORMATS_INTL.MONTH_SHORT),
      y: totals
        .filter((t) => t.year === startDate.getFullYear() && t.month === startDate.getMonth() + 1)
        .reduce((total, item) => total + item.total, 0),
    })
    startDate = addMonths(startDate, 1)
  }

  return data
}

export const getIncomeTotals = (totals: MonthlyCategoryTotals[], range?: DateRange) => {
  const incomeTotals = totals.filter(isIncome)
  // Income totals are negative values, but we want to display them as positive
  return getMonthlyTotals(incomeTotals, range).map((t) => ({ ...t, y: Math.abs(t.y) }))
}

export const getPaymentTotals = (totals: MonthlyCategoryTotals[], range?: DateRange) => {
  const paymentTotals = totals.filter(isPayment)
  // Payment totals are negative values, but we want to display them as positive
  return getMonthlyTotals(paymentTotals, range).map((t) => ({ ...t, y: Math.abs(t.y) }))
}

export const getSpendingTotals = (totals: MonthlyCategoryTotals[], range?: DateRange) => {
  const spendingTotals = totals.filter(isSpend)
  return getMonthlyTotals(spendingTotals, range)
}

export const getSpendingData = (totals: MonthlyCategoryTotals[]) => {
  const data: { x: string; y: number }[] = []

  // Filter out income and transfers
  const filteredTotals = totals.filter(
    (t) =>
      t.top_level_category_guid !== CategoryGuids.INCOME &&
      t.top_level_category_guid !== CategoryGuids.TRANSFER &&
      t.total > 0,
  )

  let startDate = startOfMonth(subMonths(new Date(), 5))
  const endDate = endOfMonth(new Date())
  while (isBefore(startDate, endDate)) {
    // Add total amount for each month
    data.push({
      x: formatDate(startDate, DATE_FORMATS_INTL.MONTH_SHORT),
      y: filteredTotals
        .filter((t) => t.year === startDate.getFullYear() && t.month === startDate.getMonth() + 1)
        .reduce((total, item) => total + item.total, 0),
    })
    startDate = addMonths(startDate, 1)
  }

  return data
}
